import { cn } from '@/lib/utils'

/**
 * A GPU configuration drawn as a lab cartridge: a CSS 3D box (front, top and
 * side face) with a fan grille, an ID plate, a status LED bar and an edge
 * connector. Every module looks a little different: the fan count follows
 * the VRAM, the LED bar the vCPUs and the hazard stripe its place in the
 * line-up. Purely decorative, so screen readers skip it.
 */
interface GpuCartridgeProps {
  /** Two-digit module number printed on the plate. */
  code: string
  name: string
  vram: number
  vcpus: number
  /** 1-based place in the line-up. */
  rank: number
  lit?: boolean
  size?: 'sm' | 'lg'
  className?: string
}

export function GpuCartridge({ code, name, vram, vcpus, rank, lit, size = 'sm', className }: GpuCartridgeProps) {
  const fans = vram >= 24 ? 2 : 1
  const leds = Math.max(2, Math.min(8, Math.round(vcpus / 2)))
  const lg = size === 'lg'

  return (
    <div aria-hidden className={cn('lab-cart', lg && 'lab-cart-lg', lit && 'is-lit', className)}>
      <div className="lab-cart-shadow" />
      <div className="lab-cart-body">
        <div className="lab-cart-top" />
        <div className="lab-cart-side" />
        <div className="lab-cart-front">
          <div className="lab-cart-hazard" style={{ width: `${10 + rank * 8}%` }} />
          <div className="lab-cart-grille">
            {Array.from({ length: fans }, (_, i) => (
              <div key={i} className="lab-fan-housing">
                <div
                  className={cn('lab-fan', lit && 'is-spinning')}
                  style={i ? { animationDirection: 'reverse' } : undefined}
                />
              </div>
            ))}
          </div>
          <div className="lab-cart-plate">
            <span className="lab-cart-code">{code}</span>
            {lg && (
              <span className="font-heading font-bold uppercase leading-[1.05] tracking-tight">
                {name}
                <span className="mt-[0.3em] block font-sans font-normal normal-case opacity-70">{vram} GB</span>
              </span>
            )}
          </div>
          <div className="lab-cart-leds">
            {Array.from({ length: leds }, (_, i) => (
              <span
                key={i}
                className={cn('lab-cart-led', lit && 'is-on')}
                style={lit ? { animationDelay: `${i * 70}ms` } : undefined}
              />
            ))}
          </div>
          <div className="lab-cart-pins" />
        </div>
      </div>
    </div>
  )
}
